import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, RefreshCw, ScrollText } from 'lucide-react';
import axios from 'axios';
import {
  fetchBasisSm21MonitorSettings,
  fetchBasisSm21Runs,
  saveBasisSm21MonitorSettings,
  SM21_POLL_LABELS,
  triggerBasisSm21Run,
} from '@/lib/basisSm21MonitorApi';
import { formatIsoDateTime } from '@/lib/sapJobStatus';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

function statusVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'success') return 'default';
  if (s === 'partial') return 'secondary';
  if (s === 'error') return 'destructive';
  return 'outline';
}

/**
 * Operations dashboard for scheduled SM21 system log reviews (not Chat Studio).
 */
export default function BasisSystemLog() {
  const [settings, setSettings] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [scheduledEnabled, setScheduledEnabled] = useState(false);
  const [pollIntervalHours, setPollIntervalHours] = useState(4);
  const [reportEmail, setReportEmail] = useState('');
  const [includeSapNotes, setIncludeSapNotes] = useState(true);
  const [allowedIntervals, setAllowedIntervals] = useState([1, 4, 6, 12, 24]);

  const pollIntervalValue = allowedIntervals.includes(pollIntervalHours)
    ? String(pollIntervalHours)
    : String(allowedIntervals[0] ?? 4);

  const loadAll = useCallback(async () => {
    setError('');
    try {
      const [settingsData, runsData] = await Promise.all([
        fetchBasisSm21MonitorSettings(),
        fetchBasisSm21Runs({ limit: 50 }),
      ]);
      setSettings(settingsData);
      setRuns(Array.isArray(runsData?.runs) ? runsData.runs : []);
      if (Array.isArray(settingsData?.allowed_poll_interval_hours)) {
        setAllowedIntervals(settingsData.allowed_poll_interval_hours);
      }
      const hours = settingsData?.poll_interval_hours ?? 4;
      const allowed = Array.isArray(settingsData?.allowed_poll_interval_hours)
        ? settingsData.allowed_poll_interval_hours
        : allowedIntervals;
      setPollIntervalHours(allowed.includes(hours) ? hours : allowed[0] ?? 4);
      setScheduledEnabled(Boolean(settingsData?.scheduled_enabled));
      const emails = settingsData?.report_emails;
      setReportEmail(
        Array.isArray(emails) && emails.length
          ? emails.join(', ')
          : String(settingsData?.report_email || ''),
      );
      setIncludeSapNotes(settingsData?.include_sap_notes !== false);
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.message
        : 'Could not load SM21 monitor data.';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const persistSettings = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const data = await saveBasisSm21MonitorSettings(patch);
      setSettings(data);
      setPollIntervalHours(data.poll_interval_hours ?? pollIntervalHours);
      setScheduledEnabled(Boolean(data.scheduled_enabled));
      setReportEmail(String(data.report_email || reportEmail));
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.message
        : 'Failed to save settings.';
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const onSaveConfig = () => {
    persistSettings({
      scheduled_enabled: scheduledEnabled,
      poll_interval_hours: pollIntervalHours,
      report_email: reportEmail.trim(),
      email_on_poll: true,
      include_sap_notes: includeSapNotes,
    });
  };

  const onRunNow = async () => {
    setRunningNow(true);
    setError('');
    try {
      await triggerBasisSm21Run();
      await loadAll();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.message
        : 'Run failed.';
      setError(String(msg));
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="basis-sm21-dashboard">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ScrollText className="h-6 w-6 text-cp-muted" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">System log (SM21)</h1>
            <p className="text-sm text-cp-muted">
              Scheduled Basis health checks across application servers — review runs and agent summaries.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={loading}
            onClick={() => {
              setLoading(true);
              loadAll();
            }}
            data-testid="basis-sm21-refresh"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={runningNow || loading}
            onClick={onRunNow}
            data-testid="basis-sm21-run-now"
          >
            {runningNow ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-1 h-4 w-4" />
            )}
            Run now
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive" data-testid="basis-sm21-settings-error">
          {error}
        </p>
      ) : null}

      <Card data-testid="basis-sm21-schedule-config">
        <CardHeader>
          <CardTitle className="text-base">Schedule &amp; reporting</CardTitle>
          <CardDescription>
            HALO polls SM21 for error and critical syslog entries only. If none are found, the report email
            still states that the window was clean. Configure frequency, recipients, and optional SAP Note
            guidance when errors exist.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="basis-sm21-scheduled-enabled"
                checked={scheduledEnabled}
                disabled={saving}
                onCheckedChange={(v) => setScheduledEnabled(Boolean(v))}
              />
              <Label htmlFor="basis-sm21-scheduled-enabled" className="font-normal">
                Enable scheduled polling
              </Label>
            </div>
            <div className="min-w-[12rem] space-y-1.5">
              <Label htmlFor="basis-sm21-poll-interval" className="text-xs text-cp-muted">
                Frequency
              </Label>
              <Select
                value={pollIntervalValue}
                disabled={saving}
                onValueChange={(v) => setPollIntervalHours(Number(v))}
              >
                <SelectTrigger id="basis-sm21-poll-interval" data-testid="basis-sm21-poll-interval">
                  <SelectValue placeholder="Every 4 hours" />
                </SelectTrigger>
                <SelectContent>
                  {allowedIntervals.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {SM21_POLL_LABELS[h] || `Every ${h} hours`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[16rem] flex-1 space-y-1.5">
              <Label htmlFor="basis-sm21-report-email" className="text-xs text-cp-muted">
                Report email(s)
              </Label>
              <Input
                id="basis-sm21-report-email"
                type="text"
                placeholder="basis-team@company.com, lead@company.com"
                value={reportEmail}
                disabled={saving}
                onChange={(e) => setReportEmail(e.target.value)}
                data-testid="basis-sm21-report-email"
              />
            </div>
            <div className="flex items-center gap-2 pb-0.5">
              <Checkbox
                id="basis-sm21-include-sap-notes"
                checked={includeSapNotes}
                disabled={saving}
                onCheckedChange={(v) => setIncludeSapNotes(Boolean(v))}
              />
              <Label htmlFor="basis-sm21-include-sap-notes" className="text-sm font-normal">
                Include SAP Note recommended fixes
              </Label>
            </div>
            <Button type="button" disabled={saving} onClick={onSaveConfig} data-testid="basis-sm21-save-config">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </div>
          {settings?.scheduler_running ? (
            <p className="text-xs text-cp-muted">Scheduler active on this API instance.</p>
          ) : null}
        </CardContent>
      </Card>

      <Card data-testid="basis-sm21-runs-table">
        <CardHeader>
          <CardTitle className="text-base">Run history</CardTitle>
          <CardDescription>
            Each row is one SM21 check — technical summary plus Basis agent narrative for that window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-cp-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading runs…
            </div>
          ) : runs.length === 0 ? (
            <p className="text-sm text-cp-muted" data-testid="basis-sm21-runs-empty">
              No runs yet. Enable scheduling or use Run now (requires sap-basis-mcp on basis-sm21).
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Completed</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead className="min-w-[280px]">Agent summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => {
                  const id = run.id || run.started_at;
                  const expanded = expandedId === id;
                  const summary = run.agent_summary || run.summary || '';
                  const notesFix = run.sap_notes_section || '';
                  const short =
                    summary.length > 160 && !expanded ? `${summary.slice(0, 160)}…` : summary;
                  return (
                    <TableRow key={id} data-testid="basis-sm21-run-row">
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatIsoDateTime(run.finished_at || run.started_at)}
                      </TableCell>
                      <TableCell className="capitalize text-xs">{run.trigger || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(run.status)}>{run.status || '—'}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-cp-muted">
                        {run.review_period?.from && run.review_period?.to
                          ? `${formatIsoDateTime(run.review_period.from)} → ${formatIsoDateTime(run.review_period.to)}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => setExpandedId(expanded ? null : id)}
                        >
                          {short || '—'}
                        </button>
                        {expanded && notesFix ? (
                          <div className="mt-2 whitespace-pre-wrap rounded border border-cp-line bg-cp-inset p-2 text-xs text-cp-muted">
                            {notesFix}
                          </div>
                        ) : null}
                        {expanded && run.summary && run.summary !== summary ? (
                          <div className="mt-2 whitespace-pre-wrap text-xs text-cp-muted">
                            <span className="font-medium text-cp-foreground">Technical: </span>
                            {run.summary}
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
